import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { Eyebrow } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { CtaBanner } from "@/components/CtaBanner";
import { Blob } from "@/components/Decor";
import { formatDate, categoryTone } from "@/pages/Insights";
import { renderRich } from "@/lib/rich.jsx";

import { applySeo, getArticleSeo } from "@/lib/seo";
import { useArticle } from "@/hooks/useArticle";

function ArticleBody({ body, tone }) {
  return body.split("\n\n").map((block, i) => {
    if (block.startsWith("### ")) {
      return <h3 key={i} className="font-display text-xl sm:text-2xl font-semibold text-[#0E1B31] mt-10 mb-3">{block.slice(4)}</h3>;
    }
    if (block.startsWith("## ")) {
      return (
        <h2 key={i} className="font-display text-2xl sm:text-3xl font-semibold text-[#0E1B31] mt-14 mb-4 flex items-baseline gap-3">
          <span className="h-2 w-2 rounded-full shrink-0" style={{ background: tone }} />
          {block.slice(3)}
        </h2>
      );
    }
    return (
      <p key={i} className="text-base lg:text-[17px] leading-[1.9] text-[#3B4A5E] mt-6">
        {renderRich(block)}
      </p>
    );
  });
}

export default function InsightArticle() {
  const { slug } = useParams();
  const { article, articleSlug, status, retry } = useArticle(slug);

  useEffect(() => {
    if (article?.slug === articleSlug) applySeo(getArticleSeo(article));
  }, [article, articleSlug]);

  if (status === 'not-found') {
    return (
      <div className="pt-48 pb-32 text-center" data-testid="article-not-found">
        <p className="font-display text-3xl text-[#0E1B31]">Το άρθρο δεν βρέθηκε.</p>
        <Link to="/insights" className="mt-6 inline-block text-sm text-[#C26D52] underline underline-offset-4">Επιστροφή στα άρθρα</Link>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="pt-48 pb-32 text-center" data-testid="article-error" role="alert">
        <p className="font-display text-3xl text-[#0E1B31]">Δεν ήταν δυνατή η φόρτωση του άρθρου. Δοκιμάστε ξανά.</p>
        <button type="button" onClick={retry} data-testid="article-retry" className="mt-6 inline-block text-sm text-[#C26D52] underline underline-offset-4">Επανάληψη</button>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pt-48 pb-32 text-center" data-testid="article-loading">
        <p className="text-sm text-[#6B7A8F]">Φόρτωση άρθρου...</p>
      </div>
    );
  }

  const tone = categoryTone(article.category);

  return (
    <div data-testid="insight-article-page">
      <section className="relative mesh-hero grain overflow-hidden pt-36 lg:pt-44 pb-12">
        <Blob color={`${tone}30`} className="-top-20 right-[10%]" size={520} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/insights" data-testid="article-back-link" className="inline-flex items-center gap-2 rounded-full border border-[#0E1B31]/10 bg-white/70 px-4 py-2 text-xs text-[#3B4A5E] hover:text-[#0E1B31] transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" /> Όλα τα άρθρα
            </Link>
            <div className="mt-10">
              <Eyebrow tone={tone}>{article.category}</Eyebrow>
            </div>
            <h1 data-testid="article-title" className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.12] tracking-tight text-[#0E1B31]">
              {article.title}
            </h1>
            <p className="mt-7 text-[13px] text-[#6B7A8F] flex items-center gap-3">
              <span>{formatDate(article.date)}</span>
              <span className="h-1 w-1 rounded-full" style={{ background: tone }} />
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {article.reading_time} ανάγνωσης</span>
              <span className="h-1 w-1 rounded-full" style={{ background: tone }} />
              <span>CRESTIA</span>
            </p>
          </motion.div>
        </div>
      </section>

      <article className="pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-4">
          <div data-testid="article-body" className="rounded-[32px] bg-white border border-[#0E1B31]/[0.07] shadow-[0_40px_80px_-50px_rgba(14,27,49,0.35)] px-7 py-10 sm:px-12 sm:py-14">
            <div className="rounded-2xl p-5 text-[15px] leading-relaxed text-[#0E1B31]" style={{ background: `${tone}14`, borderLeft: `4px solid ${tone}` }}>
              {article.excerpt}
            </div>
            <ArticleBody body={article.body} tone={tone} />
          </div>
        </div>
      </article>

      <Reveal>
        <CtaBanner
          title="Ας δούμε πώς εφαρμόζεται όλο αυτό στη δική σας επιχείρηση."
          subtitle="Κάθε επιχείρηση είναι διαφορετική — η αξία της CRESTIA είναι η προσαρμογή στη δική σας πραγματικότητα."
          buttonLabel="Ξεκινήστε τη συζήτηση"
        />
      </Reveal>
    </div>
  );
}
